import React, { useState, useEffect, useCallback } from "react";
import * as api from "../api";
import { STATUSES, getPermissions } from "../constants";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { formatPhone, sendWa, waMessage, nextStatus, generateReceiptPDF, sendReceiptViaWhatsApp, setReceiptSizesCache, setBrandingCache, shouldAskWaConsent, markWaSent, markWaDeclined } from "../utils";
import {
  Btn, IconBtn, Card, Badge, Chip, Input, Select, Spinner,
  EmptyState, Modal, Ref, Stat, SectionHeader, Callout,
} from "./UI";

// ─── Notation en étoiles (défini hors du composant : jamais de composant imbriqué) ──
function Stars({ value = 0, size = 13 }) {
  return (
    <span style={{ display: "inline-flex", gap: 1 }} title={`${value} sur 5`} aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <Icon key={i} name="star" size={size} color={i <= value ? C.amber : C.borderStrong} />
      ))}
    </span>
  );
}

// ─── Libellés / icônes de paiement ────────────────────────────────────────────
const PAY_TONE = { paye: "success", avance: "warning", non_paye: "danger" };
const PAY_LABEL = { paye: "Payé", avance: "Avance", non_paye: "Non payé" };

function Manager({ setView, user }) {
  const perms = getPermissions(user);
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [mainTab, setMainTab]       = useState("orders");
  const [subTab, setSubTab]         = useState("active");
  const [search, setSearch]         = useState("");
  const [filterStatus, setFilter]   = useState("Tous");
  const [filterPayment, setFilterPayment] = useState("Tous");
  const [filterType, setFilterType] = useState("Tous");
  const [waModal, setWaModal]       = useState(null);
  const [waLoading, setWaLoading]   = useState(false);
  const [stats, setStats]           = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [calView, setCalView]       = useState("week");
  const [calDate, setCalDate]       = useState(new Date());
  const [dayOrders, setDayOrders]   = useState(null);
  // Create order modal
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [nClient, setNClient] = useState("");
  const [nPhone, setNPhone]   = useState("");
  const [nAddr, setNAddr]     = useState("");
  const [nDate, setNDate]     = useState(new Date().toISOString().split("T")[0]);
  const [nTime, setNTime]     = useState("");
  const [nNotes, setNNotes]   = useState("");
  const [nSaving, setNSaving] = useState(false);
  const [nError, setNError]   = useState("");
  // Order items
  const [nArticles, setNArticles] = useState([]);
  const [nArtType, setNArtType]   = useState("");
  const [nArtQty, setNArtQty]     = useState(1);
  const [nPrice, setNPrice]       = useState("");
  const [nPayMethod, setNPayMethod] = useState("cash");
  const [nPayStatus, setNPayStatus] = useState("non_paye");
  const [nAmountPaid, setNAmountPaid] = useState("");
  const [nArtList, setNArtList]   = useState([]);
  const [nEtabList, setNEtabList] = useState([]);
  const [nEtab, setNEtab]         = useState("");
  const [nLoyalty, setNLoyalty]   = useState(null);
  const [nApplyDiscount, setNApplyDiscount] = useState(true);
  // Clients (Manager)
  const [mClients, setMClients] = useState([]);
  const [mClientsLoading, setMClientsLoading] = useState(false);
  const [mClientSearch, setMClientSearch] = useState("");
  const [mClientDetail, setMClientDetail] = useState(null);
  const [mClientOrders, setMClientOrders] = useState([]);
  const [mClientOrdersLoading, setMClientOrdersLoading] = useState(false);
  const [mClientSort, setMClientSort] = useState("total_orders");
  // Reviews
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  // Assignment
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignModal, setAssignModal] = useState(null); // { order, type: 'collector'|'delivery' }
  const [assignSaving, setAssignSaving] = useState(false);
  // Print size picker
  const [printSizeModal, setPrintSizeModal] = useState(null); // order to print

  const load = useCallback(async () => {
    setLoading(true);
    try { setOrders(await api.getOrders()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await api.getStats()); }
    catch (err) { console.error(err); }
    finally { setStatsLoading(false); }
  }, []);

  const loadReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const [revs, rstats] = await Promise.all([api.getReviews(50), api.getReviewStats()]);
      setReviews(revs); setReviewStats(rstats);
    } catch {}
    finally { setReviewsLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.getAssignableUsers().then(d => setAssignableUsers(d || [])).catch(() => {});
    // Load receipt font size config + branding
    api.getSettings().then(s => {
      if (s && s.receipt_font_sizes) {
        try { setReceiptSizesCache(JSON.parse(s.receipt_font_sizes)); } catch {}
      }
      if (s) setBrandingCache({ phone_main: s.phone_main, phone_whatsapp: s.phone_whatsapp });
    }).catch(() => {});
  }, [load]);
  // Lazy load stats and reviews when their tabs are active
  useEffect(() => {
    if (mainTab === "dashboard" && (perms.stats || perms.finances)) loadStats();
    if (mainTab === "clients" && perms.clients) {
      setMClientsLoading(true);
      api.adminGetClients().then(d => setMClients(d || [])).catch(() => {}).finally(() => setMClientsLoading(false));
    }
    if (mainTab === "reviews") loadReviews();
  }, [mainTab]); // eslint-disable-line

  async function advance(order) {
    const ns = nextStatus(order.status, order.order_type);
    if (!ns) return;
    try {
      const updated = await api.updateOrder(order.id, { status: ns });
      setOrders(prev => prev.map(o => o.id === order.id ? updated : o));
      // Consentement WhatsApp intelligent : fenêtre 24h
      if (shouldAskWaConsent(updated.phone)) {
        setWaModal(updated); // demander si nouveau ou fenêtre expirée
      } else {
        sendWa(updated.phone, waMessage(updated)); // déjà dans la fenêtre : envoi direct
        markWaSent(updated.phone);
      }
      loadStats();
    } catch (err) { alert(err.message); }
  }

  async function deleteOrder(id) {
    if (!window.confirm("Supprimer cette commande ?")) return;
    try {
      await api.deleteOrder(id);
      setOrders(prev => prev.filter(o => o.id !== id));
      loadStats();
    } catch (err) { alert(err.message); }
  }

  const ACTIVE_STATUS  = ["En attente","Réceptionné","Collecté","En traitement","Prêt"];
  const isActive       = o => ACTIVE_STATUS.includes(o.status);
  const filtered       = orders
    .filter(o => subTab === "active" ? isActive(o) : (o.status === "Livré" || o.status === "Récupéré"))
    .filter(o => filterStatus === "Tous" || o.status === filterStatus)
    .filter(o => filterPayment === "Tous" || o.payment_status === filterPayment)
    .filter(o => filterType === "Tous" || (filterType === "sur_place" ? o.order_type === "sur_place" : o.order_type !== "sur_place"))
    .filter(o => {
      const q = search.toLowerCase();
      return !q || o.id.toLowerCase().includes(q) || o.client.toLowerCase().includes(q) || o.phone.includes(q);
    });

  // Calendar helpers
  function getWeekDays(base) {
    const start = new Date(base);
    start.setDate(start.getDate() - start.getDay() + 1);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }
  function getMonthDays(base) {
    const year = base.getFullYear(), month = base.getMonth();
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    const days  = [];
    const start = first.getDay() === 0 ? 6 : first.getDay() - 1;
    for (let i = 0; i < start; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  }
  function ordersForDay(date) {
    if (!date) return [];
    const ds = date.toISOString().split("T")[0];
    return orders.filter(o => o.date === ds);
  }
  function fmtDay(d) {
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  }

  function printManagerRecu(o) {
    setPrintSizeModal(o);
  }

  async function managerCancelEnRoute(order) {
    if (!window.confirm(`Remettre ${order.id} en attente de collecteur ?`)) return;
    try {
      await api.updateOrder(order.id, { collector_status: null });
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, collector_status: null } : o));
    } catch (err) { alert(err.message); }
  }
  async function doPrint(o, sizeProfile) {
    setPrintSizeModal(null);
    try {
      const doc = await generateReceiptPDF(o, "a4", sizeProfile);
      doc.save(`Recu_${o.id}.pdf`);
    } catch (err) { console.error("PDF error:", err); alert("Erreur lors de la génération du reçu"); }
  }

  const WEEK_DAYS = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

  const MAIN_TABS = [
    ["orders",    "Commandes",  "order",     true],
    ["dashboard", "Dashboard",  "dashboard", perms.stats || perms.finances],
    ["clients",   "Clients",    "users",     perms.clients],
    ["reviews",   "Avis",       "star",      true],
    ["calendar",  "Calendrier", "calendar",  perms.calendar],
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      {/* ── En-tête ── */}
      <div style={{ background: C.navy, color: C.inkOn }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto", padding: "14px 16px",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: F.size.sm, color: "rgba(255,255,255,.62)" }}>Tableau de bord</p>
            <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.02em" }}>Setal Express</h1>
          </div>
          <Btn variant="white" small icon="logout" onClick={() => { api.logout(); setView("landing"); }}>Déconnexion</Btn>
        </div>
      </div>

      {/* ── Onglets ── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 16px" }}>
          <div className="tabs" style={{ borderBottom: "none" }}>
            {MAIN_TABS.filter(([, , , show]) => show).map(([k, label, icon]) => (
              <button key={k} type="button" onClick={() => setMainTab(k)}
                className={`tab ${mainTab === k ? "active" : ""}`}
                aria-current={mainTab === k ? "page" : undefined}>
                <Icon name={icon} size={15} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: 16 }}>
        {/* ── Dashboard ── */}
        {mainTab === "dashboard" && (
          statsLoading ? <Spinner /> : stats ? (
            <div className="animate-fadeInUp">
              {/* Indicateurs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 20 }}>
                {[
                  ["package",  "Commandes aujourd'hui", (stats.today?.orders || 0).toLocaleString("fr-FR"), null,    "navy",    true],
                  ["cash",     "Revenus aujourd'hui",   (stats.today?.revenue || 0).toLocaleString("fr-FR"), "FCFA", "success", perms.finances],
                  ["calendar", "Commandes ce mois",     (stats.month?.orders || 0).toLocaleString("fr-FR"), null,    "navy",    true],
                  ["trendUp",  "Revenus ce mois",       (stats.month?.revenue || 0).toLocaleString("fr-FR"), "FCFA", "accent",  perms.finances],
                ].filter(([, , , , , show]) => show).map(([icon, label, val, unit, tone]) => (
                  <Stat key={label} icon={icon} label={label} value={val} unit={unit} tone={tone} />
                ))}
              </div>

              {/* Répartition par statut */}
              {stats.byStatus && (
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader title="Par statut" icon="list" />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {Object.entries(stats.byStatus).map(([status, count]) => (
                      <div key={status} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "5px 10px", background: C.surfaceAlt,
                        border: `1px solid ${C.border}`, borderRadius: R.md,
                      }}>
                        <Badge status={status} small />
                        <span style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md, ...numeric }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Revenus — 14 derniers jours */}
              {perms.finances && stats.dailyRevenue && stats.dailyRevenue.length > 0 && (
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader title="Revenus" subtitle="14 derniers jours" icon="chart" />
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 140 }}>
                    {stats.dailyRevenue.slice(-14).map((d, i) => {
                      const max = Math.max(...stats.dailyRevenue.slice(-14).map(x => Number(x.revenue) || 0), 1);
                      const h = Math.max(3, (Number(d.revenue) / max) * 116);
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", minWidth: 0 }}>
                          <div style={{ fontSize: F.size.xs, color: C.inkMuted, marginBottom: 4, ...numeric }}>
                            {Number(d.revenue) > 0 ? `${(Number(d.revenue) / 1000).toFixed(0)}k` : ""}
                          </div>
                          <div style={{ width: "100%", height: h, background: C.navy, borderRadius: `${R.sm}px ${R.sm}px 0 0`, minWidth: 6 }} />
                          <div style={{ fontSize: F.size.xs, color: C.inkFaint, marginTop: 5, ...numeric }}>{d.date?.slice(8)}</div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Top clients & collecteurs */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                {perms.clients && stats.topClients && stats.topClients.length > 0 && (
                  <Card padded={false}>
                    <div style={{ padding: "14px 16px 0" }}><SectionHeader title="Top clients" icon="trophy" /></div>
                    <div className="tbl-wrap" style={{ border: "none" }}>
                      <table className="tbl">
                        <thead>
                          <tr><th>#</th><th>Client</th><th className="num">Dépensé</th></tr>
                        </thead>
                        <tbody>
                          {stats.topClients.slice(0, 5).map((c, i) => (
                            <tr key={i}>
                              <td style={{ color: C.inkFaint, ...numeric }}>{i + 1}</td>
                              <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{c.client}</td>
                              <td className="num" style={numeric}>{Number(c.spent || 0).toLocaleString("fr-FR")} F</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
                {perms.collectors && stats.byCollector && stats.byCollector.length > 0 && (
                  <Card padded={false}>
                    <div style={{ padding: "14px 16px 0" }}><SectionHeader title="Top collecteurs" icon="collect" /></div>
                    <div className="tbl-wrap" style={{ border: "none" }}>
                      <table className="tbl">
                        <thead>
                          <tr><th>#</th><th>Collecteur</th><th className="num">Commandes</th></tr>
                        </thead>
                        <tbody>
                          {stats.byCollector.slice(0, 5).map((c, i) => (
                            <tr key={i}>
                              <td style={{ color: C.inkFaint, ...numeric }}>{i + 1}</td>
                              <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{c.name || "Collecteur"}</td>
                              <td className="num" style={numeric}>{c.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>

              {/* Synthèse des avis */}
              {perms.clients && reviewStats && (
                <Card style={{ marginTop: 16 }}>
                  <SectionHeader title="Avis clients" icon="star" />
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: F.size["3xl"], fontWeight: F.weight.bold, color: C.ink, letterSpacing: "-.02em", ...numeric }}>
                        {reviewStats.average?.toFixed(1) || "0"}
                      </div>
                      <Stars value={Math.round(reviewStats.average || 0)} />
                    </div>
                    <div style={{ fontSize: F.size.base, color: C.inkMuted, ...numeric }}>
                      {reviewStats.total || 0} avis au total
                    </div>
                  </div>
                  {reviews.slice(0, 3).map(r => (
                    <div key={r.id} style={{ padding: "9px 0", borderTop: `1px solid ${C.border}`, fontSize: F.size.base }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 3 }}>
                        <Stars value={r.rating} size={12} />
                        <span style={{ color: C.inkFaint, fontSize: F.size.sm, ...numeric }}>
                          {new Date(r.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                      {r.comment && <p className="truncate" style={{ color: C.inkBody }}>{r.comment}</p>}
                    </div>
                  ))}
                </Card>
              )}
            </div>
          ) : (
            <Card><EmptyState icon="warning" title="Statistiques indisponibles" description="Impossible de charger les statistiques pour le moment." compact /></Card>
          )
        )}

        {/* ── Commandes ── */}
        {mainTab === "orders" && (
          <div className="animate-fadeInUp">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <div className="tabs" style={{ borderBottom: `1px solid ${C.border}` }}>
                {[["active", "Actives", "pending"], ["history", "Historique", "check"]].map(([k, label, icon]) => (
                  <button key={k} type="button" onClick={() => setSubTab(k)}
                    className={`tab ${subTab === k ? "active" : ""}`}
                    aria-current={subTab === k ? "page" : undefined}>
                    <Icon name={icon} size={14} />
                    {label}
                  </button>
                ))}
              </div>
              <Btn small icon="add" onClick={() => {
                setNClient(""); setNPhone(""); setNAddr(""); setNDate(new Date().toISOString().split("T")[0]);
                setNTime(""); setNNotes(""); setNError(""); setNArticles([]); setNPrice(""); setNPayMethod("cash"); setNPayStatus("non_paye"); setNAmountPaid(""); setNEtab("");
                api.getArticles().then(d => { setNArtList(d || []); if (d?.length) setNArtType(d[0].name); }).catch(() => {});
                api.adminGetEtablissements().then(d => { const a = (d||[]).filter(e=>e.active); setNEtabList(a); if (a.length===1) setNEtab(a[0].id.toString()); }).catch(() => {});
                setShowNewOrder(true);
              }}>Nouvelle commande</Btn>
            </div>

            {/* Filtres */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.inkFaint, pointerEvents: "none" }}>
                  <Icon name="search" size={15} />
                </span>
                <input
                  className="field"
                  value={search} onChange={e => setSearch(e.target.value)}
                  aria-label="Rechercher une commande"
                  placeholder="Rechercher nom, téléphone, commande…"
                  style={{ paddingLeft: 32 }}
                />
              </div>
              <select className="field" style={{ width: "auto" }} aria-label="Filtrer par statut"
                value={filterStatus} onChange={e => setFilter(e.target.value)}>
                <option value="Tous">Tous les statuts</option>
                {[...STATUSES, "Réceptionné", "Récupéré"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="field" style={{ width: "auto" }} aria-label="Filtrer par paiement"
                value={filterPayment} onChange={e => setFilterPayment(e.target.value)}>
                <option value="Tous">Tous les paiements</option>
                <option value="non_paye">Non payé</option>
                <option value="avance">Avance</option>
                <option value="paye">Payé</option>
                <option value="pending">En attente</option>
              </select>
              <select className="field" style={{ width: "auto" }} aria-label="Filtrer par type"
                value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="Tous">Tous les types</option>
                <option value="delivery">Collectes</option>
                <option value="sur_place">Sur place</option>
              </select>
            </div>

            {loading ? <Spinner /> : filtered.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="inbox"
                  title="Aucune commande trouvée"
                  description="Ajustez vos filtres ou créez une nouvelle commande."
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Réf</th>
                      <th>Client</th>
                      <th>Collecte</th>
                      <th>Articles</th>
                      <th>Statut</th>
                      <th className="num">Montant</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(order => {
                      const ns = nextStatus(order.status, order.order_type);
                      const items = order.items ? order.items.split(",").filter(Boolean) : [];
                      const reste = order.payment_status === "avance" && order.amount_paid && order.price
                        ? Number(order.price) - Number(order.amount_paid) : null;
                      return (
                        <tr key={order.id}>
                          <td>
                            <Ref>{order.id}</Ref>
                            {order.order_type === "sur_place" && (
                              <div style={{ marginTop: 4 }}><Chip tone="warning" icon="store" small>Sur place</Chip></div>
                            )}
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <div style={{ fontWeight: F.weight.medium, color: C.ink }}>{order.client}</div>
                            <div style={{ fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>{order.phone}</div>
                          </td>
                          <td style={{ minWidth: 150, maxWidth: 220 }}>
                            <div style={{ ...numeric, color: C.inkBody }}>{order.date} · {order.time}</div>
                            <div className="truncate" title={order.address} style={{ fontSize: F.size.sm, color: C.inkMuted, maxWidth: 210 }}>
                              {order.address}
                            </div>
                            {order.delivery_date && order.delivery_time && (
                              <div style={{ marginTop: 4 }}>
                                <Chip tone="success" icon="delivery" small>
                                  {order.delivery_date} — {order.delivery_time}
                                </Chip>
                              </div>
                            )}
                          </td>
                          <td style={{ maxWidth: 180 }}>
                            {items.length > 0 ? (
                              <span className="truncate" title={items.map(i => i.trim()).join(" · ")}
                                style={{ display: "block", maxWidth: 170, color: C.inkBody }}>
                                {items.map(i => i.trim()).join(" · ")}
                              </span>
                            ) : <span style={{ color: C.inkFaint }}>—</span>}
                          </td>
                          <td style={{ minWidth: 150 }}>
                            <Badge status={order.status} />
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, marginTop: 5 }}>
                              {order.collector_status === "en_route" && order.status === "En attente" && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <Chip tone="warning" icon="collect" small>Collecteur en route</Chip>
                                  <IconBtn icon="undo" title="Annuler le départ du collecteur" danger
                                    onClick={() => managerCancelEnRoute(order)} />
                                </span>
                              )}
                              {order.status === "Prêt" && order.order_type === "sur_place" && (
                                <Chip tone="warning" icon="store" small>À récupérer sur place</Chip>
                              )}
                              {order.status === "Prêt" && order.order_type !== "sur_place" && !order.delivery_date && (
                                <Chip tone="danger" icon="pending" small>Attente choix livraison</Chip>
                              )}
                              {order.client_confirmed_at && (
                                <Chip tone="success" icon="check" small>Confirmé par le client</Chip>
                              )}
                              {(order.status === "Livré" || order.status === "Récupéré") && !order.client_confirmed_at && (
                                <Chip tone="danger" icon="pending" small>Attente confirmation client</Chip>
                              )}
                              {order.assigned_collector_name && (
                                <Chip tone="navy" icon="user" small>{order.assigned_collector_name}</Chip>
                              )}
                              {order.assigned_delivery_name && (
                                <Chip tone="success" icon="delivery" small>{order.assigned_delivery_name}</Chip>
                              )}
                            </div>
                          </td>
                          <td className="num" style={{ minWidth: 130 }}>
                            {order.price ? (
                              <>
                                <div style={{ fontWeight: F.weight.semi, color: C.ink, ...numeric }}>
                                  {Number(order.price).toLocaleString("fr-FR")} FCFA
                                </div>
                                {order.payment_method && (
                                  <div style={{ fontSize: F.size.sm, color: C.inkMuted }}>{order.payment_method}</div>
                                )}
                                {order.payment_status && order.payment_status !== "pending" && (
                                  <div style={{ display: "inline-flex", marginTop: 4 }}>
                                    <Chip tone={PAY_TONE[order.payment_status] || "neutral"} small>
                                      {PAY_LABEL[order.payment_status] || order.payment_status}
                                      {order.payment_status === "avance" && order.amount_paid
                                        ? ` ${Number(order.amount_paid).toLocaleString("fr-FR")} F` : ""}
                                    </Chip>
                                  </div>
                                )}
                                {reste != null && (
                                  <div style={{ fontSize: F.size.sm, color: C.danger, fontWeight: F.weight.medium, marginTop: 3, ...numeric }}>
                                    Reste {reste.toLocaleString("fr-FR")} F
                                  </div>
                                )}
                              </>
                            ) : <span style={{ color: C.inkFaint }}>—</span>}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              {ns && ns !== "Récupéré" && (
                                <Btn small iconRight="forward" onClick={() => advance(order)}>{ns}</Btn>
                              )}
                              {ns === "Récupéré" && order.payment_status === "paye" && (
                                <Btn small variant="success" icon="check" onClick={() => advance(order)}>Récupéré</Btn>
                              )}
                              {ns === "Récupéré" && order.payment_status !== "paye" && (
                                <Btn small variant="accent" icon="cash" onClick={async () => {
                                  if (!window.confirm(`Encaisser ${Number(order.price || 0).toLocaleString()} FCFA et marquer comme récupéré ?`)) return;
                                  try {
                                    await api.updateOrder(order.id, { payment_status: "paye", amount_paid: Number(order.price || 0), status: "Récupéré" });
                                    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: "Récupéré", payment_status: "paye" } : o));
                                    loadStats();
                                  } catch (err) { alert(err.message); }
                                }}>Encaisser</Btn>
                              )}
                              {order.status === "En attente" && !order.assigned_collector_id && (
                                <Btn small variant="light" icon="userAdd" onClick={() => setAssignModal({ order, type: "collector" })}>Collecteur</Btn>
                              )}
                              {order.status === "Prêt" && order.order_type !== "sur_place" && !order.assigned_delivery_id && (
                                <Btn small variant="light" icon="delivery" onClick={() => setAssignModal({ order, type: "delivery" })}>Livreur</Btn>
                              )}
                              {(order.payment_status === "avance" || order.payment_status === "non_paye") && order.price && (
                                <Btn small variant="success" icon="coins" onClick={async () => {
                                  const reste2 = order.payment_status === "avance" ? Number(order.price) - Number(order.amount_paid || 0) : Number(order.price);
                                  if (!window.confirm(`Le client a payé le reste de ${reste2.toLocaleString()} FCFA ?`)) return;
                                  try {
                                    await api.updateOrder(order.id, { payment_status: "paye", amount_paid: Number(order.price) });
                                    const updated = await api.getOrder(order.id);
                                    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, payment_status: "paye", amount_paid: order.price } : o));
                                    printManagerRecu(updated);
                                    if (perms.stats || perms.finances) loadStats();
                                  } catch (err) { alert(err.message); }
                                }}>Solde</Btn>
                              )}
                              <IconBtn icon="phone" title="Appeler le client" variant="outline"
                                onClick={() => window.location.href = `tel:+${formatPhone(order.phone)}`} />
                              <IconBtn icon="chat" title="Envoyer un WhatsApp" variant="outline"
                                onClick={() => sendWa(order.phone, waMessage(order))} />
                              <IconBtn icon="attach" title="Envoyer le reçu PDF par WhatsApp" variant="outline"
                                disabled={waLoading}
                                onClick={() => sendReceiptViaWhatsApp(order, setWaLoading, (err) => { if (err) alert("Erreur : " + err); })} />
                              <IconBtn icon="print" title="Imprimer le reçu" variant="outline"
                                onClick={() => printManagerRecu(order)} />
                              {perms.delete_orders && (
                                <IconBtn icon="delete" title="Supprimer la commande" variant="outline" danger
                                  onClick={() => deleteOrder(order.id)} />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Clients ── */}
        {mainTab === "clients" && perms.clients && (
          <div className="animate-fadeInUp">
            <SectionHeader title="Base clients" icon="users" />

            <div style={{ position: "relative", marginBottom: 14 }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.inkFaint, pointerEvents: "none" }}>
                <Icon name="search" size={15} />
              </span>
              <input
                className="field"
                type="text"
                value={mClientSearch}
                aria-label="Rechercher un client"
                placeholder="Rechercher par nom, téléphone ou adresse…"
                onChange={e => {
                  setMClientSearch(e.target.value);
                  const val = e.target.value;
                  clearTimeout(window._mClientSearchTimeout);
                  window._mClientSearchTimeout = setTimeout(() => {
                    setMClientsLoading(true);
                    api.adminGetClients(val).then(d => setMClients(d || [])).catch(() => {}).finally(() => setMClientsLoading(false));
                  }, 400);
                }}
                style={{ paddingLeft: 32 }}
              />
            </div>

            {!mClientsLoading && mClients.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 14 }}>
                <Stat icon="users" label="Clients" tone="navy" value={mClients.length.toLocaleString("fr-FR")} />
                <Stat icon="order" label="Commandes" tone="success"
                  value={mClients.reduce((s, c) => s + (c.total_orders || 0), 0).toLocaleString("fr-FR")} />
                <Stat icon="gift" label="Avec points" tone="accent"
                  value={mClients.filter(c => c.loyalty_points > 0).length.toLocaleString("fr-FR")} />
              </div>
            )}

            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: F.size.sm, color: C.inkMuted, marginRight: 2 }}>Trier par</span>
              {[["total_orders", "Commandes", "order"], ["total_spent", "Dépenses", "money"], ["loyalty_points", "Points", "gift"], ["last_order_at", "Récent", "clock"]].map(([k, label, icon]) => (
                <button key={k} type="button" onClick={() => setMClientSort(k)} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "5px 10px", borderRadius: R.md,
                  border: `1px solid ${mClientSort === k ? C.navy : C.border}`,
                  background: mClientSort === k ? C.navySoft : C.surface,
                  color: mClientSort === k ? C.navy : C.inkMuted,
                  fontWeight: F.weight.medium, fontSize: F.size.sm, cursor: "pointer",
                }} aria-pressed={mClientSort === k}>
                  <Icon name={icon} size={13} />{label}
                </button>
              ))}
            </div>

            {/* Fiche client */}
            {mClientDetail && (
              <Modal
                title={mClientDetail.name || "Client"}
                icon="user"
                width={620}
                onClose={() => { setMClientDetail(null); setMClientOrders([]); }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 16 }}>
                  <Stat icon="mobile" label="Téléphone" value={<span style={{ fontSize: F.size.lg }}>{mClientDetail.phone}</span>} />
                  <Stat icon="order" label="Commandes" tone="success" value={(mClientDetail.total_orders || 0).toLocaleString("fr-FR")} />
                  <Stat icon="money" label="Total dépensé" tone="accent" unit="FCFA"
                    value={Number(mClientDetail.total_spent || 0).toLocaleString("fr-FR")} />
                  <Stat icon="gift" label="Points fidélité" tone="navy" unit="pts"
                    value={(mClientDetail.loyalty_points || 0).toLocaleString("fr-FR")} />
                  <Stat icon="location" label="Adresse" value={<span style={{ fontSize: F.size.md, fontWeight: F.weight.medium }}>{mClientDetail.address || "—"}</span>} />
                  <Stat icon="calendar" label="Client depuis"
                    value={<span style={{ fontSize: F.size.md, fontWeight: F.weight.medium }}>
                      {mClientDetail.first_order_at ? new Date(mClientDetail.first_order_at).toLocaleDateString("fr-FR") : "—"}
                    </span>} />
                </div>

                <SectionHeader title="Historique des commandes" icon="list" />
                {mClientOrdersLoading ? <Spinner /> : mClientOrders.length === 0 ? (
                  <EmptyState icon="inbox" title="Aucune commande" compact />
                ) : (
                  <div className="tbl-wrap" style={{ maxHeight: 300, overflowY: "auto" }}>
                    <table className="tbl">
                      <thead>
                        <tr><th>Réf</th><th>Date</th><th>Statut</th><th className="num">Montant</th></tr>
                      </thead>
                      <tbody>
                        {mClientOrders.map(o => (
                          <tr key={o.id}>
                            <td>
                              <Ref>{o.id}</Ref>
                              {o.items && <div className="truncate" style={{ fontSize: F.size.sm, color: C.inkFaint, maxWidth: 180 }}>{o.items}</div>}
                            </td>
                            <td style={numeric}>{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
                            <td><Badge status={o.status} small /></td>
                            <td className="num" style={numeric}>
                              {o.price ? `${Number(o.price).toLocaleString("fr-FR")} F` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Modal>
            )}

            {mClientsLoading ? <Spinner /> : mClients.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="users"
                  title={mClientSearch ? "Aucun client trouvé" : "Aucun client"}
                  description={mClientSearch ? "Essayez un autre nom ou numéro." : "Les clients apparaîtront dès la première commande."}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Téléphone</th>
                      <th className="num">Commandes</th>
                      <th className="num">Dépensé</th>
                      <th className="num">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...mClients].sort((a, b) => {
                      if (mClientSort === "total_orders") return (b.total_orders || 0) - (a.total_orders || 0);
                      if (mClientSort === "total_spent") return Number(b.total_spent || 0) - Number(a.total_spent || 0);
                      if (mClientSort === "loyalty_points") return (b.loyalty_points || 0) - (a.loyalty_points || 0);
                      if (mClientSort === "last_order_at") return new Date(b.last_order_at || 0) - new Date(a.last_order_at || 0);
                      return 0;
                    }).map(c => (
                      <tr key={c.phone} className="row-click"
                        onClick={() => {
                          setMClientDetail(c);
                          setMClientOrdersLoading(true);
                          api.adminGetClientOrders(c.phone).then(d => setMClientOrders(d || [])).catch(() => {}).finally(() => setMClientOrdersLoading(false));
                        }}>
                        <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{c.name || "Sans nom"}</td>
                        <td style={{ color: C.inkMuted, ...numeric }}>{c.phone}</td>
                        <td className="num" style={numeric}>{c.total_orders}</td>
                        <td className="num" style={numeric}>{Number(c.total_spent || 0).toLocaleString("fr-FR")} F</td>
                        <td className="num" style={numeric}>
                          {c.loyalty_points > 0
                            ? <Chip tone="navy" small>{c.loyalty_points} pts</Chip>
                            : <span style={{ color: C.inkFaint }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Avis clients ── */}
        {mainTab === "reviews" && (
          reviewsLoading ? <Spinner /> : (
            <div className="animate-fadeInUp">
              <SectionHeader title="Avis clients" icon="star" />

              {reviewStats && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 14 }}>
                    <Stat icon="star" label="Note moyenne" tone="accent" unit="/ 5"
                      value={reviewStats.average?.toFixed(1) || "—"} />
                    <Stat icon="chat" label="Total avis" tone="navy"
                      value={(reviewStats.total || 0).toLocaleString("fr-FR")} />
                  </div>

                  {reviewStats.distribution && (
                    <Card style={{ marginBottom: 16 }}>
                      <SectionHeader title="Répartition des notes" icon="chart" />
                      <div style={{ display: "grid", gap: 8 }}>
                        {[5, 4, 3, 2, 1].map(star => {
                          const count = reviewStats.distribution[star] || 0;
                          const pct = reviewStats.total ? (count / reviewStats.total) * 100 : 0;
                          return (
                            <div key={star} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Stars value={star} size={12} />
                              <div style={{ flex: 1, height: 7, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.pill, overflow: "hidden" }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: star >= 4 ? C.success : star === 3 ? C.amber : C.danger }} />
                              </div>
                              <span style={{ minWidth: 34, textAlign: "right", fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}
                </>
              )}

              {reviews.length === 0 ? (
                <Card padded={false}>
                  <EmptyState icon="chat" title="Aucun avis pour le moment"
                    description="Les avis laissés par vos clients s'afficheront ici." />
                </Card>
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr><th>Commande</th><th>Téléphone</th><th>Note</th><th>Commentaire</th><th>Date</th></tr>
                    </thead>
                    <tbody>
                      {reviews.map(r => (
                        <tr key={r.id}>
                          <td><Ref>{r.order_id}</Ref></td>
                          <td style={{ color: C.inkMuted, ...numeric }}>{r.phone || "—"}</td>
                          <td><Stars value={r.rating} /></td>
                          <td style={{ maxWidth: 360, color: C.inkBody }}>{r.comment || <span style={{ color: C.inkFaint }}>—</span>}</td>
                          <td style={{ color: C.inkMuted, whiteSpace: "nowrap", ...numeric }}>
                            {new Date(r.created_at).toLocaleString("fr-FR")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        )}

        {/* ── Calendrier ── */}
        {mainTab === "calendar" && (
          <div className="animate-fadeInUp">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn small variant="white" icon="back" onClick={() => {
                  const d = new Date(calDate);
                  d.setDate(d.getDate() - (calView === "week" ? 7 : 30));
                  setCalDate(d); setDayOrders(null);
                }}>Préc.</Btn>
                <Btn small variant="white" onClick={() => { setCalDate(new Date()); setDayOrders(null); }}>Aujourd'hui</Btn>
                <Btn small variant="white" iconRight="forward" onClick={() => {
                  const d = new Date(calDate);
                  d.setDate(d.getDate() + (calView === "week" ? 7 : 30));
                  setCalDate(d); setDayOrders(null);
                }}>Suiv.</Btn>
              </div>
              <div className="tabs" style={{ borderBottom: `1px solid ${C.border}` }}>
                {[["week", "Semaine"], ["month", "Mois"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => { setCalView(k); setDayOrders(null); }}
                    className={`tab ${calView === k ? "active" : ""}`}
                    aria-pressed={calView === k}>{l}</button>
                ))}
              </div>
            </div>

            <Card style={{ marginBottom: 16 }}>
              {calView === "week" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
                  {WEEK_DAYS.map(d => (
                    <div key={d} style={{ fontSize: F.size.xs, fontWeight: F.weight.semi, color: C.inkMuted, textTransform: "uppercase", letterSpacing: ".04em", paddingBottom: 6 }}>{d}</div>
                  ))}
                  {getWeekDays(calDate).map((day, i) => {
                    const dayO = ordersForDay(day);
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                      <button key={i} type="button" onClick={() => setDayOrders({ date: day, orders: dayO })}
                        style={{
                          border: `1px solid ${isToday ? C.navy : C.border}`, borderRadius: R.md, padding: 8,
                          minHeight: 70, cursor: "pointer", background: isToday ? C.navySoft : C.surface,
                          textAlign: "left", display: "block", width: "100%",
                        }}>
                        <div style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: isToday ? C.navy : C.inkBody, marginBottom: 5, ...numeric }}>
                          {day.getDate()}
                        </div>
                        {dayO.length > 0 && <Chip tone="navy" small>{dayO.length} cmd</Chip>}
                      </button>
                    );
                  })}
                </div>
              )}
              {calView === "month" && (
                <>
                  <div style={{ fontWeight: F.weight.semi, color: C.ink, marginBottom: 12, fontSize: F.size.lg }}>
                    {calDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
                    {WEEK_DAYS.map(d => (
                      <div key={d} style={{ fontSize: F.size.xs, fontWeight: F.weight.semi, color: C.inkMuted, textTransform: "uppercase", letterSpacing: ".04em", paddingBottom: 4 }}>{d}</div>
                    ))}
                    {getMonthDays(calDate).map((day, i) => {
                      if (!day) return <div key={i} />;
                      const dayO = ordersForDay(day);
                      const isToday = day.toDateString() === new Date().toDateString();
                      return (
                        <button key={i} type="button" onClick={() => setDayOrders({ date: day, orders: dayO })}
                          style={{
                            border: `1px solid ${isToday ? C.navy : C.border}`, borderRadius: R.sm,
                            padding: "4px 6px", minHeight: 46, cursor: "pointer",
                            background: isToday ? C.navySoft : C.surface,
                            textAlign: "left", display: "block", width: "100%",
                          }}>
                          <div style={{ fontSize: F.size.sm, fontWeight: F.weight.semi, color: isToday ? C.navy : C.inkBody, ...numeric }}>
                            {day.getDate()}
                          </div>
                          {dayO.length > 0 && (
                            <span style={{
                              display: "inline-block", marginTop: 3, fontSize: F.size.xs,
                              background: C.navy, color: C.inkOn, borderRadius: R.sm, padding: "0 5px",
                              fontWeight: F.weight.medium, ...numeric,
                            }}>{dayO.length}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>

            {dayOrders && (
              <Card padded={false}>
                <div style={{ padding: "14px 16px 0" }}>
                  <SectionHeader
                    title={fmtDay(dayOrders.date)}
                    subtitle={`${dayOrders.orders.length} commande(s)`}
                    icon="calendar"
                    actions={<IconBtn icon="close" title="Fermer le détail du jour" onClick={() => setDayOrders(null)} />}
                  />
                </div>
                {dayOrders.orders.length === 0 ? (
                  <EmptyState icon="calendar" title="Aucune commande ce jour" compact />
                ) : (
                  <div className="tbl-wrap" style={{ border: "none" }}>
                    <table className="tbl">
                      <thead>
                        <tr><th>Réf</th><th>Client</th><th>Statut</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {dayOrders.orders.map(o => {
                          const ns = nextStatus(o.status, o.order_type);
                          return (
                            <tr key={o.id}>
                              <td><Ref>{o.id}</Ref></td>
                              <td>
                                <div style={{ fontWeight: F.weight.medium, color: C.ink }}>{o.client}</div>
                                <div style={{ fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>{o.phone}</div>
                              </td>
                              <td><Badge status={o.status} /></td>
                              <td>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  {ns && (
                                    <Btn small iconRight="forward" onClick={() => {
                                      advance(o);
                                      setDayOrders(prev => ({ ...prev, orders: prev.orders.map(x => x.id === o.id ? { ...x, status: ns } : x) }));
                                    }}>{ns}</Btn>
                                  )}
                                  <Btn small variant="light" icon="chat" onClick={() => sendWa(o.phone, waMessage(o))}>WhatsApp</Btn>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </div>

      {/* ── Modale WhatsApp ── */}
      {waModal && (() => {
        const isExpired = (() => {
          try {
            const raw = localStorage.getItem(`wa_consent_${waModal.phone}`);
            if (!raw) return false;
            const { opted_in, last_sent } = JSON.parse(raw);
            return opted_in && (Date.now() - last_sent) > 24 * 60 * 60 * 1000;
          } catch { return false; }
        })();
        const isKnown = !!localStorage.getItem(`wa_consent_${waModal.phone}`);
        return (
          <Modal title="Notifier le client ?" icon="chat" width={440}>
            {isExpired && (
              <Callout tone="warning" icon="timer" style={{ marginBottom: 12 }}>
                Fenêtre WhatsApp expirée (&gt;24h) — une nouvelle confirmation est nécessaire.
              </Callout>
            )}
            {!isKnown && (
              <Callout tone="info" icon="info" style={{ marginBottom: 12 }}>
                Premier contact — demander le consentement au client.
              </Callout>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: F.size.base, color: C.inkMuted }}>Commande</span>
              <Ref>{waModal.id}</Ref>
              <Badge status={waModal.status} />
              {waModal.order_type === "sur_place"
                ? <Chip tone="warning" icon="store" small>Sur place</Chip>
                : <Chip tone="navy" icon="scooter" small>Collecte</Chip>}
            </div>
            <div style={{
              background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
              padding: 12, marginBottom: 16, fontSize: F.size.base, color: C.inkBody,
              lineHeight: 1.65, whiteSpace: "pre-wrap",
            }}>
              {waMessage(waModal)}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="success" icon="chat" style={{ flex: 1 }} onClick={() => {
                sendWa(waModal.phone, waMessage(waModal));
                markWaSent(waModal.phone);
                setWaModal(null);
              }}>
                Envoyer WhatsApp
              </Btn>
              <Btn variant="ghost" onClick={() => {
                markWaDeclined(waModal.phone);
                setWaModal(null);
              }}>Plus tard</Btn>
            </div>
          </Modal>
        );
      })()}

      {/* ── Modale : assigner collecteur / livreur ── */}
      {assignModal && (
        <Modal
          title={assignModal.type === "collector" ? "Assigner un collecteur" : "Assigner un livreur"}
          icon={assignModal.type === "collector" ? "userAdd" : "delivery"}
          width={440}
          onClose={() => setAssignModal(null)}
          footer={<Btn variant="ghost" onClick={() => setAssignModal(null)}>Annuler</Btn>}
        >
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 14 }}>
            Commande <Ref>{assignModal.order.id}</Ref> — {assignModal.order.client}
          </p>
          {assignableUsers.length === 0 ? (
            <EmptyState icon="users" title="Aucun utilisateur disponible" compact />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
              {assignableUsers.map(u => (
                <button key={u.id} type="button" disabled={assignSaving} onClick={async () => {
                  setAssignSaving(true);
                  try {
                    if (assignModal.type === "collector") {
                      await api.assignCollector(assignModal.order.id, u.id);
                    } else {
                      await api.assignDelivery(assignModal.order.id, u.id);
                    }
                    setAssignModal(null);
                    load();
                  } catch (err) { alert("Erreur : " + err.message); }
                  finally { setAssignSaving(false); }
                }} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                  border: `1px solid ${C.border}`, borderRadius: R.md, background: C.surface,
                  cursor: assignSaving ? "not-allowed" : "pointer", textAlign: "left", width: "100%",
                  transition: "border-color .15s ease, background .15s ease",
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.background = C.surfaceHover; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: R.md, flexShrink: 0,
                    background: C.surfaceAlt, border: `1px solid ${C.border}`, color: C.inkMuted,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name={u.role === "collector" ? "collect" : "userCheck"} size={16} />
                  </span>
                  <span>
                    <span style={{ display: "block", fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md }}>{u.name}</span>
                    <span style={{ display: "block", fontSize: F.size.sm, color: C.inkMuted }}>
                      {u.role === "collector" ? "Collecteur" : "Manager"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* ── Modale : nouvelle commande ── */}
      {showNewOrder && (
        <Modal title="Nouvelle commande" icon="add" width={560} onClose={() => setShowNewOrder(false)}>
          {nError && <Callout tone="danger" icon="warning" style={{ marginBottom: 14 }}>{nError}</Callout>}

          <Input label="Nom du client" value={nClient} onChange={setNClient} placeholder="Prénom Nom" required />
          <Input label="Téléphone" value={nPhone} onChange={setNPhone} placeholder="77 123 45 67" required icon="mobile" onBlur={() => {
            const p = nPhone.replace(/\s/g, "");
            if (p.length >= 9) {
              api.getLoyalty(p).then(l => setNLoyalty(l)).catch(() => setNLoyalty(null));
              // Auto-fill from saved client info
              api.getClientInfo(p).then(c => {
                if (c && !nClient) setNClient(c.name);
                if (c && !nAddr) setNAddr(c.address || "");
              }).catch(() => {});
            }
          }} />
          <Input label="Adresse" value={nAddr} onChange={setNAddr} placeholder="Adresse de collecte" required icon="location" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input label="Date" value={nDate} onChange={setNDate} type="date" min={new Date().toISOString().split("T")[0]} required />
            <Input label="Créneau" value={nTime} onChange={setNTime} placeholder="09:00 - 11:00" required />
          </div>

          {/* Articles */}
          <div style={{ marginBottom: 16 }}>
            <Select label="Articles" required value={nArtType} onChange={setNArtType} style={{ marginBottom: 8 }}>
              {nArtList.filter(a => a.active !== false).map(a => (
                <option key={a.name} value={a.name}>{a.name} — {Number(a.price).toLocaleString("fr-FR")} F</option>
              ))}
            </Select>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input className="field" type="number" min="1" value={nArtQty} aria-label="Quantité"
                onChange={e => setNArtQty(e.target.value)}
                style={{ width: 72, textAlign: "center", ...numeric }} />
              <Btn icon="add" style={{ flex: 1 }} onClick={() => {
                if (!nArtType || nArtQty < 1) return;
                const art = nArtList.find(a => a.name === nArtType);
                const unitPrice = art ? parseFloat(art.price) : 1000;
                setNArticles(prev => {
                  const ex = prev.find(i => i.type === nArtType);
                  if (ex) return prev.map(i => i.type === nArtType ? { ...i, qty: i.qty + Number(nArtQty) } : i);
                  return [...prev, { type: nArtType, qty: Number(nArtQty), unitPrice }];
                });
                setNArtQty(1);
              }}>Ajouter</Btn>
            </div>
            {nArticles.length > 0 && (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr><th>Article</th><th className="num">Qté</th><th className="num">Total</th><th /></tr>
                  </thead>
                  <tbody>
                    {nArticles.map((item, i) => (
                      <tr key={i}>
                        <td style={{ color: C.ink }}>{item.type}</td>
                        <td className="num" style={numeric}>{item.qty}</td>
                        <td className="num" style={numeric}>{(item.qty * item.unitPrice).toLocaleString("fr-FR")} F</td>
                        <td style={{ width: 40 }}>
                          <IconBtn icon="close" title={`Retirer ${item.type}`} danger
                            onClick={() => setNArticles(prev => prev.filter((_, j) => j !== i))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: C.surfaceAlt }}>
                      <td colSpan={2} style={{ padding: "10px 12px", fontWeight: F.weight.semi, color: C.ink, borderTop: `1px solid ${C.border}` }}>Total</td>
                      <td className="num" style={{ padding: "10px 12px", fontWeight: F.weight.bold, color: C.ink, borderTop: `1px solid ${C.border}`, ...numeric }}>
                        {nArticles.reduce((s, i) => s + i.qty * i.unitPrice, 0).toLocaleString("fr-FR")} FCFA
                      </td>
                      <td style={{ borderTop: `1px solid ${C.border}` }} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Prix modifiable */}
          <Input label="Prix total" hint="FCFA" icon="money"
            value={nPrice || (nArticles.length ? String(nArticles.reduce((s, i) => s + i.qty * i.unitPrice, 0)) : "")}
            onChange={setNPrice} type="number" placeholder="Auto-calculé" />

          {/* Remise fidélité */}
          {nLoyalty && nLoyalty.config?.enabled && nLoyalty.applicableDiscount > 0 && (() => {
            const basePrice = nPrice ? Number(nPrice) : nArticles.reduce((s, i) => s + i.qty * i.unitPrice, 0);
            return basePrice > 0 ? (
              <div style={{
                background: C.amberSoft, border: `1px solid ${C.amberBorder}`,
                borderRadius: R.md, padding: 12, marginBottom: 16,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: F.weight.semi, color: C.amberHover, fontSize: F.size.base }}>
                    <Icon name="gift" size={15} />
                    Fidélité −{nLoyalty.applicableDiscount} % ({nLoyalty.points} pts)
                  </span>
                  <Btn small variant={nApplyDiscount ? "ghost" : "accent"} onClick={() => setNApplyDiscount(d => !d)}>
                    {nApplyDiscount ? "Retirer" : "Appliquer"}
                  </Btn>
                </div>
                {nApplyDiscount && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                    <span style={{ fontSize: F.size.sm, color: C.inkMuted, textDecoration: "line-through", ...numeric }}>
                      {basePrice.toLocaleString("fr-FR")} FCFA
                    </span>
                    <span style={{ fontSize: F.size.lg, fontWeight: F.weight.bold, color: C.success, ...numeric }}>
                      {Math.round(basePrice * (1 - nLoyalty.applicableDiscount / 100)).toLocaleString("fr-FR")} FCFA
                    </span>
                  </div>
                )}
              </div>
            ) : null;
          })()}

          {/* Établissement */}
          {nEtabList.length > 0 && (
            <Select label="Établissement" value={nEtab} onChange={setNEtab} placeholder="— Sélectionner —">
              {nEtabList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </Select>
          )}

          {/* Paiement */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Select label="Paiement" value={nPayStatus} onChange={setNPayStatus}>
              <option value="non_paye">Non payé</option>
              <option value="avance">Avance</option>
              <option value="paye">Payé</option>
            </Select>
            <Select label="Méthode" value={nPayMethod} onChange={setNPayMethod}>
              <option value="cash">Espèces</option>
              <option value="wave">Wave</option>
              <option value="om">Orange Money</option>
              <option value="card">Carte</option>
            </Select>
          </div>
          {nPayStatus === "avance" && (
            <Input label="Montant avance" hint="FCFA" value={nAmountPaid} onChange={setNAmountPaid} type="number" placeholder="Montant versé" />
          )}

          <Input label="Notes" value={nNotes} onChange={setNNotes} placeholder="Instructions spéciales…" />

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn style={{ flex: 1 }} icon="check" loading={nSaving} disabled={nSaving} onClick={async () => {
              if (!nClient || !nPhone || !nAddr || !nDate || !nTime) { setNError("Veuillez remplir tous les champs obligatoires"); return; }
              if (nArticles.length === 0) { setNError("Ajoutez au moins un article"); return; }
              setNSaving(true); setNError("");
              try {
                const basePrice = nPrice ? Number(nPrice) : nArticles.reduce((s, i) => s + i.qty * i.unitPrice, 0);
                const hasDiscount = nApplyDiscount && nLoyalty?.config?.enabled && nLoyalty?.applicableDiscount > 0;
                const totalPrice = hasDiscount ? Math.round(basePrice * (1 - nLoyalty.applicableDiscount / 100)) : basePrice;
                const itemsStr = nArticles.map(i => `${i.type} x${i.qty}`).join(", ");
                const created = await api.createOrder({ client: nClient, phone: nPhone, address: nAddr, date: nDate, time: nTime, notes: nNotes, order_type: "sur_place" });
                // Manager orders -> directly "Réceptionné" with items & price (sur place)
                await api.updateOrder(created.id, {
                  status: "Réceptionné", collector_status: "arrived", order_type: "sur_place",
                  items: itemsStr, price: totalPrice,
                  payment_status: nPayStatus,
                  payment_method: nPayMethod,
                  amount_paid: nPayStatus === "paye" ? totalPrice : nPayStatus === "avance" ? Number(nAmountPaid) || 0 : 0,
                  loyalty_discount: hasDiscount ? basePrice - totalPrice : 0,
                  ...(nEtab ? { etab_id: parseInt(nEtab) } : {}),
                });
                // Sauvegarder les articles avec prix unitaires (pour le reçu PDF)
                try {
                  await api.saveOrderItems(created.id, nArticles.map(item => ({
                    article_name: item.type,
                    quantity: item.qty,
                    unit_price: item.unitPrice,
                    total_price: item.qty * item.unitPrice,
                  })));
                } catch {}
                // Deduct loyalty points
                if (hasDiscount && nLoyalty.applicableThreshold) {
                  try { await api.useLoyaltyPoints(nPhone, nLoyalty.applicableThreshold.points, created.id); } catch {}
                }
                // Auto-print receipt for sur place orders
                const receiptOrder = await api.getOrder(created.id);
                printManagerRecu(receiptOrder);
                setShowNewOrder(false);
                load();
                if (perms.stats || perms.finances) loadStats();
              } catch (err) { setNError(err.message); }
              finally { setNSaving(false); }
            }}>{nSaving ? "Création…" : "Créer la commande"}</Btn>
            <Btn variant="ghost" onClick={() => setShowNewOrder(false)}>Annuler</Btn>
          </div>
        </Modal>
      )}

      {/* ── Modale : taille d'impression ── */}
      {printSizeModal && (
        <Modal
          title="Taille d'impression"
          icon="print"
          width={400}
          onClose={() => setPrintSizeModal(null)}
          footer={<Btn variant="ghost" onClick={() => setPrintSizeModal(null)}>Annuler</Btn>}
        >
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 16 }}>
            Choisissez la taille adaptée à votre imprimante.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn style={{ flex: 1 }} icon="document" onClick={() => doPrint(printSizeModal, "normal")}>Normal</Btn>
            <Btn style={{ flex: 1 }} variant="white" icon="document" onClick={() => doPrint(printSizeModal, "grand")}>Grand</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Manager;
